import { Box } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import ReactFlow, {
  Background,
  ConnectionMode,
  Controls,
  MiniMap,
  Panel,
} from 'reactflow';
import ProjectPackage from '../../ProjectPackage';
import CustomConnectionLine from './CustomConnectionLine';
import CustomEdge from './CustomEdge';

/** Deploy status at a glance — the minimap dot is the ledger, not the render. */
const miniNodeColor = (node) => {
  if (node?.data?.isTemp) return 'transparent';
  switch (node?.data?.deploy_status) {
    case 'DEPLOYED':
      return '#43a047';
    case 'DEPLOYING':
    case 'PENDING':
      return '#fdd835';
    case 'FAILED':
      return '#e53935';
    default:
      return '#5c6470';
  }
};

const nodeTypes = {
  // Pass through React Flow's isConnectable (default true) so port handles work.
  custom: ProjectPackage,
};

const edgeTypes = {
  custom: CustomEdge,
};

const FlowCanvas = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onEdgesDelete,
  onConnect,
  onConnectStart,
  onConnectEnd,
  onConnectCheck,
  onInit,
  onDrop,
  onDragOver,
  reactFlowWrapper,
  selectedId,
  onSelectNode,
}) => {
  const [showMap, setShowMap] = useState(false);
  const handleChanges = useCallback(
    (changes) => {
      const selected = changes.find(
        (change) => change.type === 'select' && change.selected
      );
      if (selected) onSelectNode?.(selected.id);
      else if (changes.some(change => change.type === 'select' && change.id === selectedId && !change.selected)) onSelectNode?.(null);
      onNodesChange(changes);
    },
    [onNodesChange, onSelectNode, selectedId]
  );
  const styledNodes = nodes.map((node) => ({
    ...node,
    style: node.data.isTemp
      ? {
          opacity: 0.5,
          pointerEvents: 'none',
        }
      : node.style,
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        minHeight: 0,
        position: 'relative',
      }}
    >
      <div
        style={{ flexGrow: 1, height: '100%', minHeight: 0 }}
        ref={reactFlowWrapper}
      >
        <ReactFlow
          nodes={styledNodes}
          onNodeClick={(_, node) => onSelectNode?.(node.id)}
          onPaneClick={() => onSelectNode?.(null)}
          edges={edges}
          onNodesChange={handleChanges}
          onEdgesChange={onEdgesChange}
          onEdgesDelete={onEdgesDelete}
          deleteKeyCode={['Backspace', 'Delete']}
          edgesFocusable
          edgesUpdatable={false}
          onConnect={onConnect}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          isValidConnection={onConnectCheck}
          onInit={onInit}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          connectionLineComponent={CustomConnectionLine}
          connectionMode={ConnectionMode.Strict}
          fitView
          fitViewOptions={{ padding: 0.12, maxZoom: 0.95 }}
          minZoom={0.25}
          maxZoom={2}
        >
          <Panel position="top-left">
            <div className="graph-mode-label">
              <span /> Workflow canvas
            </div>
          </Panel>
          <Panel position="top-right">
            <Tooltip title="Toggle minimap">
              <IconButton
                className="graph-map-toggle"
                size="small"
                aria-label="Toggle minimap"
                aria-pressed={showMap}
                onClick={() => setShowMap((v) => !v)}
              >
                <MapOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Panel>

          <Controls />
          {showMap && (
            <MiniMap
              pannable={false}
              zoomable={false}
              nodeColor={miniNodeColor}
              nodeStrokeWidth={2}
              style={{ width: 168, height: 116 }}
            />
          )}
          <Background
            variant="dots"
            gap={24}
            size={1}
            color="var(--canvas-border)"
          />
        </ReactFlow>
      </div>
    </Box>
  );
};

export default FlowCanvas;
